import axios from 'axios';
import { API_CONFIG } from './config';
import { mockSubmitQuestionnaire } from './mockApi';

// Toggle this to use mock API for testing without backend
const USE_MOCK_API = import.meta.env.VITE_USE_MOCK_API === 'true';

/**
 * Submits the patient questionnaire data to the Lambda function
 * @param {Object} formData - The complete form data
 * @returns {Promise} - API response
 */
export const submitQuestionnaire = async (formData) => {
  // Use mock API if enabled (for testing without backend)
  if (USE_MOCK_API) {
    console.log('🧪 Using Mock API for testing');
    return mockSubmitQuestionnaire(formData);
  }
  
  try {
    // Transform the form data to match Lambda expectations
    const payload = {
      // Personal Information
      FullName: formData.fullName,
      Age: parseInt(formData.age, 10),
      Gender: formData.gender,
      DOB: formData.dateOfBirth, // Format: YYYY-MM-DD
      Email: formData.email,
      
      // Pain Information
      PainBegin: formData.painBegin, // Format: YYYY-MM-DD
      HowOften: formData.frequency, // Maps to 'Frequency' field
      
      // Multi-select arrays (already in correct format)
      CharacterPainRaw: formData.characterPain,
      RadiateRaw: formData.radiate,
      WorsenedByRaw: formData.worsenedBy,
      RelievedByRaw: formData.relievedBy,
      
      // Risk Factors
      Smoke: formData.smoke,
      RiskFactorsRaw: formData.riskFactors,
    };

    console.log('Submitting payload:', payload);

    const response = await axios.post(API_CONFIG.endpoint, payload, {
      timeout: API_CONFIG.timeout,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    console.log('✅ Response received:', response.data);
    
    // Parse the response body if it's a string (Lambda returns stringified body)
    let parsedResponse = response.data;
    if (typeof parsedResponse.body === 'string') {
      try {
        parsedResponse.body = JSON.parse(parsedResponse.body);
      } catch (e) {
        console.warn('Could not parse response body:', e);
      }
    }
    
    // Extract the result if nested
    if (parsedResponse.body && parsedResponse.body.result) {
      return parsedResponse.body;
    } else if (parsedResponse.result) {
      return parsedResponse;
    }

    return parsedResponse;
  } catch (error) {
    console.error('API Error:', error);
    
    if (error.response) {
      // Server responded with error status
      throw new Error(error.response.data?.error || 'Server error occurred');
    } else if (error.request) {
      // Request made but no response
      throw new Error('No response from server. Please check your connection.');
    } else {
      // Something else went wrong
      throw new Error(error.message || 'An unexpected error occurred');
    }
  }
};

/**
 * Validates the form data before submission
 * @param {Object} formData - The form data to validate
 * @returns {Object} - { isValid: boolean, errors: Object }
 */
export const validateFormData = (formData) => {
  const errors = {};

  // Personal information validation
  if (!formData.fullName || formData.fullName.trim().length < 2) {
    errors.fullName = 'Please enter your full name';
  }

  if (!formData.age || formData.age < 18 || formData.age > 120) {
    errors.age = 'Please enter a valid age (18-120)';
  }

  if (!formData.gender) {
    errors.gender = 'Please select your gender';
  }

  if (!formData.dateOfBirth) {
    errors.dateOfBirth = 'Please enter your date of birth';
  }

  // Email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!formData.email || !emailRegex.test(formData.email)) {
    errors.email = 'Please enter a valid email address';
  }

  // Pain information validation
  if (!formData.painBegin) {
    errors.painBegin = 'Please enter when your pain began';
  }

  if (!formData.frequency) {
    errors.frequency = 'Please select how often you experience pain';
  }

  // Multi-select validation (at least one option required)
  if (!formData.characterPain || formData.characterPain.length === 0) {
    errors.characterPain = 'Please select at least one option';
  }

  if (!formData.radiate || formData.radiate.length === 0) {
    errors.radiate = 'Please select at least one option';
  }

  if (!formData.worsenedBy || formData.worsenedBy.length === 0) {
    errors.worsenedBy = 'Please select at least one option';
  }

  if (!formData.relievedBy || formData.relievedBy.length === 0) {
    errors.relievedBy = 'Please select at least one option';
  }

  // Smoking status
  if (!formData.smoke) {
    errors.smoke = 'Please select your smoking status';
  }

  // Risk factors
  if (!formData.riskFactors || formData.riskFactors.length === 0) {
    errors.riskFactors = 'Please select at least one option';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};
